import { DatePipe } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import { Platform } from '@angular/cdk/platform';
import { map, Observable, Subscription } from 'rxjs';

import { AppConstants } from '@root/app/app.constants';
import { AuthService, LoaderService, MultilingualService } from '@core/services';
import { IMemberRegistrationRequest, CUSTOMER_SUPPORT_ENUM, BrandConfig } from '@models/interfaces';
import { AppCenterAnalytics, AppInsightsService, CmsService, CustomValidatorService, MemberService } from '@shared/services';
import { contactNumberPattern, emailPattern, memberIdPattern, namePattern, rxGroupPattern, userNamePattern } from '@shared/utils';
import { SampleCardIdDialogComponent } from '../sample-card-id-dialog/sample-card-id-dialog.component';
import { BRAND_CONFIG } from '@core/injection-tokens';

@Component({
    selector: 'app-registration',
    templateUrl: './registration.component.html',
    styleUrls: ['./registration.component.scss'],
    providers: [DatePipe],
    standalone: false
})
export class RegistrationComponent implements OnInit, OnDestroy {
  @ViewChild('stepper') stepper!: MatStepper;
  public memberDetailsForm: FormGroup;
  public cardDetailsForm: FormGroup;
  public contactDetailsForm: FormGroup;
  public maxDate = new Date();
  public eligibilityCheckAPIErr = '';
  public registrationAPIErr = '';
  public progressBarValues = [0, 25, 50];
  public selectedLanguage!: string;
  private subscription: Subscription = new Subscription();
  public memberRecordExists!: boolean;
  public technicalSupportNumber$!: Observable<string>;

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly formBuilder: FormBuilder,
    private readonly activatedRoute: ActivatedRoute,
    public readonly multilingualService: MultilingualService,
    private readonly memberService: MemberService,
    private readonly loaderService: LoaderService,
    private readonly datePipe: DatePipe,
    private readonly appInsightsService: AppInsightsService,
    private cmsService: CmsService,
    private readonly dialog: MatDialog,
    private platform: Platform,
    @Inject(BRAND_CONFIG) private brandConfig: BrandConfig
  ) {
    this.memberDetailsForm = this.formBuilder.group({
      firstName: [
        '',
        [
          Validators.required,
          Validators.maxLength(50),
          Validators.pattern(namePattern),
        ],
      ],
      lastName: [
        '',
        [
          Validators.required,
          Validators.maxLength(50),
          Validators.pattern(namePattern),
        ],
      ],
      DOB: ['', [Validators.required, CustomValidatorService.dateValidator()]],
    });
    this.cardDetailsForm = this.formBuilder.group({
      rxGroup: [
        '',
        [
          Validators.required,
          Validators.maxLength(7),
          Validators.pattern(rxGroupPattern),
        ],
      ],
      memberId: [
        '',
        [
          Validators.required,
          Validators.maxLength(20),
          Validators.pattern(memberIdPattern),
        ],
      ],
    });
    this.contactDetailsForm = this.formBuilder.group({
      userName: ['', [Validators.required, Validators.pattern(userNamePattern)]],
      contactNumber: [
        '',
        [
          Validators.required,
          Validators.minLength(10),
          Validators.maxLength(10),
          Validators.pattern(contactNumberPattern)
        ],
      ],
      email: ['', [Validators.required, Validators.pattern(emailPattern)]],
    });
  }

  public ngOnInit(): void {
    this.appInsightsService.trackPageViewWithoutClientId(
      `${AppCenterAnalytics.REGISTRATION_PAGE_VIEWED}`,
      this.getProperties()
    );
    this.subscription.add(
      this.activatedRoute.queryParams.subscribe((params) => {
        if (params?.ui_locales) {
          this.multilingualService.getLangMap(params.ui_locales, true);
          this.selectedLanguage = params.ui_locales;
        } else {
          this.multilingualService.getLangMap(
            AppConstants.LANGUAGE.DEFAULT,
            true
          );
          this.selectedLanguage = AppConstants.LANGUAGE.DEFAULT;
        }
      })
    );

    this.subscription.add(
      this.multilingualService.langMapObservable.subscribe(() =>
        this.emitEventForMobileView()
      )
    );

    this.technicalSupportNumber$ = this.cmsService.getCustomerSupport().pipe(
      map((res) => {
        const techSupportNumber = (res || []).find(
          (x) => x.key === CUSTOMER_SUPPORT_ENUM.LABEL_COMMON_TECHNICAL_SUPPORT
        )?.supportNumber;
        return techSupportNumber ? techSupportNumber : '';
      })
    );
  }

  public handleBackNavigation(): void {
    this.appInsightsService.trackPageViewWithoutClientId(
      `${AppCenterAnalytics.REGISTRATION_PAGE_BACK_BUTTON_CLICKED}`
    );

    if (this.stepper.selectedIndex) {
      this.stepper.selectedIndex = this.stepper.selectedIndex - 1;
    }
    this.eligibilityCheckAPIErr = '';
    this.registrationAPIErr = '';
  }

  public goToLogin(): void {
    this.appInsightsService.trackPageViewWithoutClientId(
      `${AppCenterAnalytics.LOGIN_CLICKED}`,
      this.getProperties()
    );
    
    this.subscription.add(
      this.authService
        .checkForB2CActiveAccount()
        .subscribe(() =>
          this.authService.navigateToLoginPage(this.selectedLanguage)
        )
    );
  }

  public goToGuestScreen(): void {
    this.appInsightsService.trackPageViewWithoutClientId(
      `${AppCenterAnalytics.CONTINUE_AS_GUEST}`,
      this.getProperties()
    );
    this.router.navigate(['/guest-login']);
  }

  public goToNextStep(): void {
    this.appInsightsService.trackPageViewWithoutClientId(
      `${AppCenterAnalytics.PERSONAL_DETAILS_ENTERED}`,
      this.getProperties(true)
    );
    this.stepper.selectedIndex = 2;
  }

  public checkEligibility(): void {
    this.loaderService.show();
    this.appInsightsService.trackPageViewWithoutClientId(
      `${AppCenterAnalytics.MEMBER_CARD_DETAILS_ENTERED}`,
      this.getProperties(true)
    );
    this.memberService
      .checkMemberEligibility({
        clientId: this.cardDetailsForm.value.rxGroup,
        cardId: this.cardDetailsForm.value.memberId,
        firstName: this.memberDetailsForm.value.firstName?.trim(),
        lastName: this.memberDetailsForm.value.lastName?.trim(),
        birthDate: this.datePipe.transform(
          this.memberDetailsForm.value.DOB,
          'MM-dd-yyyy'
        ) as string,
      }, this.brandConfig.oauthConfig.clientId)
      .subscribe({
        next: (res) => {
          this.loaderService.hide();
          if (
            res.memberRecordExists &&
            !res.memberDetails.isRegistered &&
            res.status == 202
          ) {
            // member's registration is complete, however isRegistered is false. So, navigate member to login
            this.eligibilityCheckAPIErr =
              this.multilingualService.langMap.LABEL_REGISTRATION_ALREADY_REGISTERED;
          } else {
            if (res.memberRecordExists) {
              this.contactDetailsForm.setValue({
                userName: res.memberDetails.userName,
                contactNumber: res.memberDetails.contactNumber,
                email: res.memberDetails.email,
              });
              this.contactDetailsForm.controls.userName.disable();
            }
            this.memberRecordExists = res.memberRecordExists;
          }
          this.stepper.selectedIndex = 3;
        },
        error: (err) => {
          this.loaderService.hide();
          this.eligibilityCheckAPIErr = err
            ? err
            : this.multilingualService.langMap
                .ERROR_COMMON_SOMETHING_WENT_WRONG;
          this.appInsightsService.trackPageViewWithoutClientId(
            `${AppCenterAnalytics.ELIGIBILITY_CHECK_ERROR}`,
            this.getProperties(true, err)
          );
        },
      });
  }

  public viewSampleIdCard(): void {
    this.appInsightsService.trackPageViewWithoutClientId(
      `${AppCenterAnalytics.SAMPLE_ID_CARD_VIEWED}`,
      this.getProperties()
    );
    this.dialog.open(SampleCardIdDialogComponent, {
      disableClose: true,
    });
  }

  public callPreRegisterAPI(): void {
    this.loaderService.show();
    this.appInsightsService.trackPageViewWithoutClientId(
      `${AppCenterAnalytics.CONTACT_DETAILS_ENTERED}`,
      this.getProperties(true)
    );
    const memberRegisterRequest: IMemberRegistrationRequest = {
      clientId: this.cardDetailsForm.value.rxGroup,
      cardId: this.cardDetailsForm.value.memberId,
      firstName: this.memberDetailsForm.value.firstName?.trim(),
      lastName: this.memberDetailsForm.value.lastName?.trim(),
      birthDate: this.datePipe.transform(
        this.memberDetailsForm.value.DOB,
        'MM-dd-yyyy'
      ) as string,
      username: this.contactDetailsForm.getRawValue().userName?.trim(),
      email: this.contactDetailsForm.value.email?.trim(),
      contactNumber: this.contactDetailsForm.value.contactNumber,
    };
    this.memberService.registerMember(memberRegisterRequest).subscribe({
      next: (res) => {
        const payload = res;
        this.subscription.add(
          this.authService
            .checkForB2CActiveAccount()
            .subscribe(() =>
              this.authService.navigateToB2CRegistrationPage(
                payload,
                this.selectedLanguage
              )
            )
        );
      },
      error: (err) => {
        this.loaderService.hide();
        this.appInsightsService.trackPageViewWithoutClientId(
          `${AppCenterAnalytics.PRE_REGISTRATION_ERROR}`,
          this.getProperties(true, err)
        );
        this.registrationAPIErr = err
          ? err
          : this.multilingualService.langMap.ERROR_COMMON_SOMETHING_WENT_WRONG;
      },
    });
  }

  public getProperties(
    logMemberDetails?: boolean,
    err?: string
  ): Record<string, any> {
    let properties: Record<string, any> = {};

    if (logMemberDetails) {
      properties = {
          userID: this.contactDetailsForm.getRawValue().userName,
          Email: this.contactDetailsForm.value.email,
          Name: `${this.memberDetailsForm.value.firstName} ${this.memberDetailsForm.value.lastName}`,
          clientID: this.cardDetailsForm.value.rxGroup,
          CardID: this.cardDetailsForm.value.memberId,
          data: {
            ...this.memberDetailsForm.value,
            ...this.cardDetailsForm.value,
            ...this.contactDetailsForm.value,
          }
        }
    }
    if (err) {
      properties['Error'] = err;
    }
    return properties;
  }

  public isMobile(): boolean {
    return this.platform.ANDROID || this.platform.IOS;
  }

  public isWeb(): boolean {
    return !this.isMobile();
  }

  private emitEventForMobileView(): void {
    // post message to react native web view to manage header for the given page
    (window as any).ReactNativeWebView?.postMessage(
      JSON.stringify({
        url: this.router.url,
        type: 'WEB_B2C_FORM_LOADED',
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
